<template>
  <v-dialog
    v-model="dialog"
    :max-width="700"
    @keydown.esc="close"
  >
    <v-card>
      <v-toolbar dense>
        <v-tabs v-model="selectedTab" fixed-tabs class="px-4">
          <v-tab>{{ $t("dialog.travel_expenses.tab.base") }}</v-tab>
          <v-tab>{{ $t("dialog.travel_expenses.tab.days") }}</v-tab>
        </v-tabs>
        <v-btn @click="store" elevation="0" color="primary">
          {{ $t("dialog.travel_expenses.store") }}
        </v-btn>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-tabs-items v-model="selectedTab">
        <v-tab-item class="overflow-y-auto">
          <validation-observer ref="form_validation">
            <v-container style="max-width: 600px; min-height: 60vh; max-height: 60vh">
              <v-row>
                <v-col>
                  <validation-provider
                    v-slot="{ errors }"
                    name="dialog.travel_expenses.place_from"
                    rules="max:120"
                  >
                    <v-text-field
                      v-model="placeFrom"
                      :counter="120"
                      :error-messages="errors"
                      :label="$t('dialog.travel_expenses.place_from')"
                      clearable
                    ></v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <validation-provider
                    v-slot="{ errors }"
                    name="dialog.travel_expenses.place_to"
                    rules="max:120"
                  >
                    <v-text-field
                      v-model="placeTo"
                      :counter="120"
                      :error-messages="errors"
                      :label="$t('dialog.travel_expenses.place_to')"
                      clearable
                    ></v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <validation-provider
                    v-slot="{ errors }"
                    name="dialog.travel_expenses.purpose"
                    rules="max:200"
                  >
                    <v-text-field
                      v-model="purpose"
                      :counter="200"
                      :error-messages="errors"
                      :label="$t('dialog.travel_expenses.purpose')"
                      clearable
                    ></v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <validation-provider
                    v-slot="{ errors }"
                    name="dialog.travel_expenses.deposit"
                    rules="decimal:2"
                  >
                    <v-text-field
                      v-model="deposit"
                      :error-messages="errors"
                      :label="$t('dialog.travel_expenses.deposit')"
                      clearable
                    ></v-text-field>
                  </validation-provider>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <v-menu
                    v-model="depositDateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="formatDate(depositDate)"
                        :label="$t('dialog.travel_expenses.deposit_date')"
                        v-bind="attrs"
                        v-on="on"
                        clearable
                        @click:clear="depositDate = null"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="depositDate"
                      @input="depositDateMenu = false"
                      no-title
                      scrollable
                      :first-day-of-week="1"
                      :locale="getIsoLocale()"
                    >
                    </v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-container>
          </validation-observer>
        </v-tab-item>
        <v-tab-item>
          <v-card>
            <v-virtual-scroll ref="vscroll" :items="days" bench="64" min-height="60vh" max-height="60vh"
                              item-height="40">
              <template v-slot:default="{ item }">
                <v-list-item dense link class="px-6">
                  <v-list-item-action>
                    <v-checkbox
                      v-model="item.active"
                      color="primary"
                    ></v-checkbox>
                  </v-list-item-action>

                  <v-list-item-content>
                    <v-list-item-title style="font-size: 100%">{{ formatDate(item.day) }} <span
                      :class="{ 'red--text': isWeek(item.day), 'blue--text': !isWeek(item.day) }">{{ formatDay(item.day)
                      }}</span></v-list-item-title>
                  </v-list-item-content>

                  <v-list-item-icon>
                    <v-checkbox
                      v-model="item.breakfast"
                      color="primary"
                      :label="$t('dialog.travel_expenses.breakfast')"
                      class="mt-0"
                    />

                    <v-checkbox
                      v-model="item.lunch"
                      color="primary"
                      :label="$t('dialog.travel_expenses.lunch')"
                      class="ml-6 mt-0"
                    />

                    <v-checkbox
                      v-model="item.dinner"
                      color="primary"
                      :label="$t('dialog.travel_expenses.dinner')"
                      class="ml-6 mt-0"
                    />
                  </v-list-item-icon>

                </v-list-item>
              </template>
            </v-virtual-scroll>

          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-dialog>
</template>

<script>
import { DateTime } from "luxon";
import utils from "@/utils";
import i18n from "@/plugins/i18n";

export default {
  name: "TravelExpensesDialog",

  data() {
    return {
      dialog: false,
      selectedTab: 0,
      depositDateMenu: false,

      placeFrom: null,
      placeTo: null,
      purpose: null,
      deposit: null,
      depositDate: null,
      days: []
    };
  },

  methods: {
    open(period, values) {
      this.dialog = true;
      //this.selectedTab = 0;

      this.placeFrom = values.placeFrom;
      this.placeTo = values.placeTo;
      this.purpose = values.purpose;
      this.deposit = utils.numberToString(values.deposit);
      this.depositDate = values.depositDate;
      this.days = values.days;

      this.refreshDays(period, values.days);
    },

    async store() {
      if (await this.$refs.form_validation.validate()) {
        this.$emit("values", {
          placeFrom: this.placeFrom,
          placeTo: this.placeTo,
          purpose: this.purpose,
          deposit: utils.toNumber(this.deposit),
          depositDate: this.depositDate || null,
          days: this.days
        });

        this.dialog = false;
      }
    },

    close() {
      this.dialog = false;
    },

    getIsoLocale() {
      return i18n.messages[i18n.locale].lang.iso;
    },

    formatDate(day) {
      if (day !== undefined && day !== null)
        return DateTime.fromISO(day).toLocaleString(DateTime.DATE_SHORT);
      else
        return null;
    },

    formatDay(day) {
      return DateTime.fromISO(day).toFormat("ccc");
    },

    isWeek(day) {
      const dn = DateTime.fromISO(day).weekday;
      return dn === 6 || dn === 7;
    },

    refreshDays(period, days) {
      if (period === null || period === undefined)
        this.days = [];
      else {
        var ds = [];
        var d = period.from;

        if (days === null || days === undefined)
          days = [];

        do {
          var ed;

          if ((ed = days.find(x => DateTime.fromISO(d).equals(DateTime.fromISO(x.day)))) !== undefined)
            ds.push(ed);
          else
            ds.push({ active: true, day: d, breakfast: false, lunch: false, dinner: false });

          d = DateTime.fromISO(d).plus({ days: 1 }).toFormat("yyyy-MM-dd");
        } while (DateTime.fromISO(d) <= DateTime.fromISO(period.to));

        this.days = ds;
      }
    }
  }

};
</script>

<style scoped>

</style>